import { Skeleton } from "../Skeleton";
import {
  useDeleteHistoryMutation,
  useGetHistoryQuery,
} from "../../libs/history/api";

import { MeasureCalculatedLeanBodyMassRow, MeasureRow } from "./Rows";
import {
  BodyMeasureKeys,
  PlicometryMeasureKeys,
} from "../../libs/measures/types";
import { DeleteButton, PlusButton } from "../Button";
import { useState } from "react";
import { usePersistedState } from "../../hooks/usePersistedState";
import { SetHistoryModal } from "./SetHistoryModal";
import { MeasureChart } from "./MeasureChart";

type HistoryTableProps = {
  userId?: number | null;
};

type Tab = "peso" | "misurazioni" | "pliche";

// Componente select anno riusabile
const YearFilter: React.FC<{
  years: string[];
  value: string;
  onChange: (year: string) => void;
}> = ({ years, value, onChange }) => {
  if (years.length <= 1) return null;
  return (
    <div className="flex items-center gap-2 mb-3">
      <span className="text-xs text-gray-500 dark:text-gray-400">Anno:</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="text-xs border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 dark:text-gray-200 focus:ring-blue-500 focus:border-blue-500"
      >
        <option value="all">Tutti</option>
        {years.map((year) => (
          <option key={year} value={year}>{year}</option>
        ))}
      </select>
    </div>
  );
};

const getYears = (dates: Set<string>) =>
  Array.from(new Set(Array.from(dates).map((d) => d.split("-")[0]))).sort().reverse();

const filterByYear = (dates: Set<string>, year: string) =>
  year === "all" ? dates : new Set(Array.from(dates).filter((d) => d.startsWith(year)));

export const HistoryTable: React.FC<HistoryTableProps> = ({ userId }) => {
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = usePersistedState<Tab>(`fitshape:chart:${userId}:historyTab`, "peso");
  const [weightYearFilter, setWeightYearFilter] = usePersistedState<string>(`fitshape:chart:${userId}:weightYear`, "all");
  const [measureYearFilter, setMeasureYearFilter] = usePersistedState<string>(`fitshape:chart:${userId}:measureYear`, "all");
  const [plicheYearFilter, setPlicheYearFilter] = usePersistedState<string>(`fitshape:chart:${userId}:plicheYear`, "all");
  const { data: history, isLoading, error } = useGetHistoryQuery(userId || -1);
  const [deleteHistory, { isLoading: isLoadingDeleteHistory }] =
    useDeleteHistoryMutation();

  if (isLoading) {
    return <Skeleton />;
  }
  if (error || !history) {
    return <div>Error...</div>;
  }
  const { measureHistoryList, weightHistoryList } = history;

  const weightDates = new Set(Object.keys(weightHistoryList).sort().reverse());
  const measureDates = new Set(Object.keys(measureHistoryList).sort().reverse());

  const weightYears = getYears(weightDates);
  const measureYears = getYears(measureDates);

  const filteredWeightDates = filterByYear(weightDates, weightYearFilter);
  const filteredMeasureDates = filterByYear(measureDates, measureYearFilter);
  const filteredPlicheDates = filterByYear(measureDates, plicheYearFilter);

  const handleDeleteWeight = (date: string) => {
    weightHistoryList[date] &&
      deleteHistory(weightHistoryList[date].historyId || -1);
  };

  const handleDeleteMeasure = (date: string) => {
    measureHistoryList[date] &&
      deleteHistory(measureHistoryList[date].historyId || -1);
  };

  const formatDate = (date: string) =>
    date.split("-")[2] + "/" + date.split("-")[1] + "/" + date.split("-")[0];

  const hasWeight = weightDates.size > 0;
  const hasMeasures = measureDates.size > 0;
  const hasData = hasWeight || hasMeasures;

  return (
    <div className="relative overflow-x-auto rounded-lg my-8">
      <div className="mb-6">
        <PlusButton variant="default" onClick={() => setShowModal(true)}>
          Aggiungi misurazione
        </PlusButton>
      </div>

      {!hasData ? (
        <div className="text-xs text-gray-700 uppercase dark:text-gray-400 mt-8">
          Non ci sono misurazioni
        </div>
      ) : (
        <>
          {/* Tab bar + filtro anno globale */}
          <div className="flex items-center gap-4 mb-4">
          <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 w-fit">
            <button
              onClick={() => setActiveTab("peso")}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === "peso"
                  ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              }`}
            >
              Peso
              {hasWeight && (
                <span className="ml-1.5 text-xs text-gray-400">({weightDates.size})</span>
              )}
            </button>
            <button
              onClick={() => setActiveTab("misurazioni")}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === "misurazioni"
                  ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              }`}
            >
              Misurazioni
              {hasMeasures && (
                <span className="ml-1.5 text-xs text-gray-400">({measureDates.size})</span>
              )}
            </button>
            <button
              onClick={() => setActiveTab("pliche")}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === "pliche"
                  ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              }`}
            >
              Pliche
              {hasMeasures && (
                <span className="ml-1.5 text-xs text-gray-400">({measureDates.size})</span>
              )}
            </button>
          </div>
          </div>

          {/* Tab Peso */}
          {activeTab === "peso" && (
            hasWeight ? (
              <>
              <MeasureChart
                dates={filteredWeightDates}
                historyList={weightHistoryList}
                measureKeys={["weight"]}
                unit="KG"
                title="Andamento Peso"
                showWeeklyAverage
                storageKey={`fitshape:chart:${userId}:timeFilter:peso`}
              />
              <YearFilter years={weightYears} value={weightYearFilter} onChange={setWeightYearFilter} />
              <table className="w-full text-sm text-gray-500 dark:text-gray-400 text-center">
                <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                  <tr>
                    <th className="px-6 py-3 text-center" />
                    {Array.from(filteredWeightDates).map((date) => (
                      <th key={date + "-del"} className="px-6 py-3 text-center">
                        <DeleteButton
                          onClick={() => handleDeleteWeight(date)}
                          isPending={isLoadingDeleteHistory}
                          variant="icon"
                        />
                      </th>
                    ))}
                  </tr>
                  <tr>
                    <th className="px-6 py-3 text-center">INFO</th>
                    {Array.from(filteredWeightDates).map((date) => (
                      <th key={date} className="px-6 py-3 text-center">
                        {formatDate(date)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <MeasureRow
                    index={0}
                    dates={filteredWeightDates}
                    name="weight"
                    historyList={weightHistoryList}
                  />
                </tbody>
              </table>
              </>
            ) : (
              <p className="text-sm text-gray-400 mt-4">Nessun dato peso inserito</p>
            )
          )}

          {/* Tab Misurazioni */}
          {activeTab === "misurazioni" && (
            hasMeasures ? (
              <>
              <MeasureChart
                dates={filteredMeasureDates}
                historyList={measureHistoryList}
                measureKeys={Object.keys(BodyMeasureKeys)}
                unit="CM"
                title="Andamento Misurazioni"
                storageKey={`fitshape:chart:${userId}:timeFilter:misurazioni`}
              />
              <YearFilter years={measureYears} value={measureYearFilter} onChange={setMeasureYearFilter} />
              <table className="w-full text-sm text-gray-500 dark:text-gray-400 text-center">
                <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                  <tr>
                    <th className="px-6 py-3 text-center" />
                    {Array.from(filteredMeasureDates).map((date) => (
                      <th key={date + "-del"} className="px-6 py-3 text-center">
                        <DeleteButton
                          onClick={() => handleDeleteMeasure(date)}
                          isPending={isLoadingDeleteHistory}
                          variant="icon"
                        />
                      </th>
                    ))}
                  </tr>
                  <tr>
                    <th className="px-6 py-3 text-center">INFO</th>
                    {Array.from(filteredMeasureDates).map((date) => (
                      <th key={date} className="px-6 py-3 text-center">
                        {formatDate(date)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(BodyMeasureKeys).map((key, index) => (
                    <MeasureRow
                      key={key}
                      index={index}
                      dates={filteredMeasureDates}
                      name={key as keyof typeof BodyMeasureKeys}
                      historyList={measureHistoryList}
                    />
                  ))}
                </tbody>
              </table>
              </>
            ) : (
              <p className="text-sm text-gray-400 mt-4">Nessuna misurazione inserita</p>
            )
          )}

          {/* Tab Pliche */}
          {activeTab === "pliche" && (
            hasMeasures ? (
              <>
              <MeasureChart
                dates={filteredPlicheDates}
                historyList={measureHistoryList}
                measureKeys={Object.keys(PlicometryMeasureKeys)}
                unit="MM"
                title="Andamento Pliche"
                storageKey={`fitshape:chart:${userId}:timeFilter:pliche`}
              />
              <YearFilter years={measureYears} value={plicheYearFilter} onChange={setPlicheYearFilter} />
              <table className="w-full text-sm text-gray-500 dark:text-gray-400 text-center">
                <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                  <tr>
                    <th className="px-6 py-3 text-center" />
                    {Array.from(filteredPlicheDates).map((date) => (
                      <th key={date + "-del"} className="px-6 py-3 text-center">
                        <DeleteButton
                          onClick={() => handleDeleteMeasure(date)}
                          isPending={isLoadingDeleteHistory}
                          variant="icon"
                        />
                      </th>
                    ))}
                  </tr>
                  <tr>
                    <th className="px-6 py-3 text-center">INFO</th>
                    {Array.from(filteredPlicheDates).map((date) => (
                      <th key={date} className="px-6 py-3 text-center">
                        {formatDate(date)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(PlicometryMeasureKeys).map((key, index) => (
                    <MeasureRow
                      key={key}
                      index={index}
                      dates={filteredPlicheDates}
                      name={key as keyof typeof PlicometryMeasureKeys}
                      historyList={measureHistoryList}
                    />
                  ))}
                  <MeasureCalculatedLeanBodyMassRow
                    dates={filteredPlicheDates}
                    index={0}
                    measureHistoryList={measureHistoryList}
                    weightHistoryList={weightHistoryList}
                  />
                </tbody>
              </table>
              </>
            ) : (
              <p className="text-sm text-gray-400 mt-4">Nessuna plicometria inserita</p>
            )
          )}
        </>
      )}
      <SetHistoryModal
        closeModal={() => setShowModal(false)}
        showModal={showModal}
      />
    </div>
  );
};
